import React, { useEffect, useMemo, useState } from 'react'
import { cn, getUserType, toCamelCase } from '@utils/safeTs'
import styles from './FlussoPersonalizzazioneApprovazioneBozzePosizione.module.sass'
import { Button, CronologiaButton, FormField, FormTextarea, StatoOrdine, FormFile, Alert, PosizionePersonalizzazioneOrdineStatus, MyAccountImagePreview } from '@components/atoms'
import { ChevronDown, Upload, Bin, Checkmark, AlertTriangle, Close, Info, Download } from '@components/icons'
import { OrdineDetailType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneType, RootStateType } from '@utils/types'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import MyAccountStatusLogModal from '../MyAccountStatusLogModal/MyAccountStatusLogModal'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { api } from '@utils/index'
import { Form, Formik } from 'formik'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { useRouter } from 'next/router'
import FlussoPersonalizzazionePosizione from '../FlussoPersonalizzazionePosizione/FlussoPersonalizzazionePosizione'
import { EDITOR_FILE_EXTENSIONS } from '@utils/editor-bozze'
import FlussoPersonalizzazioneNotaValue from '@components/atoms/FlussoPersonalizzazioneNotaValue/FlussoPersonalizzazioneNotaValue'
import FlussoPersonalizzazionePosizioneConfigurazione from '../FlussoPersonalizzazionePosizioneConfigurazione/FlussoPersonalizzazionePosizioneConfigurazione'


interface Props {
  posizione: PosizionePersonalizzazioneType
  personalizzazione: PersonalizzazioneOrdineType
  setOrdine: (p: OrdineDetailType) => void
  setStatus: ((v: '' | 'unsaved' | 'completed') => void)
  personalizzazioniOpen?: number[]
  setPersonalizzazioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  posizioniOpen: number[]
  setPosizioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  className?: string
  condizioniServizioUrl?: string | null
}

const FlussoPersonalizzazioneApprovazioneBozzePosizione = (props: Props) => {

  const { 
    posizione,
    personalizzazione,
    setOrdine,
    setStatus,
    posizioniOpen,
    setPosizioniOpen,
    className = '',
    condizioniServizioUrl = null,
  } = props
  
  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const userType = 'cliente' // in approvazione sono sempre 'cliente' (potrebbe non essere nemmeno loggato)

  const getIsEditable = (pos, userType) => pos.statoPosizionePersonalizzazione === 'BOZZA_INVIATA' && ['admin', 'commerciale', 'cliente', undefined].includes(userType)
  const isEditable = useMemo(() => getIsEditable(posizione, userType), [userType, posizione])

  const [logOpen, setLogOpen] = useState(false)
  const [edited, setEdited] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const status = useMemo(() => edited ? 'unsaved' : '', [edited])
  const allowedExtensions = useMemo(
    () => (posizione.tecnica?.estensioniAmmesse || EDITOR_FILE_EXTENSIONS)
      .map((extension) => extension?.trim()?.toLowerCase())
      .filter(Boolean),
    [posizione.tecnica?.estensioniAmmesse]
  )
  const logoAccept = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(',') : undefined
  const formatiConsentitiLabel = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(', ') : ""
  const loghiEditorClean = useMemo(() => { // Loghi dell'editor a cui viene rimosso quello della posizione se presente
    return posizione.editorState?.loghi?.filter(l => l.fileOriginale?.name !== posizione.logo.name).map((logo) => logo.fileOriginale).filter(Boolean) || []
  }, [posizione])

  const getInitialValues = (p) => ({
    note: '',
    logo: p.logo || undefined,
    fileLogo: undefined,
  })   

  const initialValues = getInitialValues(posizione)

  // Salvataggio logo
  const save = (v) => {
    let formData = new FormData()
    Object.keys(v).forEach((k) => {
      formData.append(k, v[k])
    })
    formData.append("status", submitStatus)
    api
      .put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${posizione.id}/update_status/`, formData, {
        params: {
          ...(hashParam ? { hash: hashParam } : {}),
        },
      })
      .then((res) => {
        const newOrdine = toCamelCase(res.data) as OrdineDetailType
        setOrdine(newOrdine)
        setEdited(false)
        displayNotification(
          `posizione_aggiornata_${posizione.id}`, 
          t("Posizione {0} aggiornata correttamente", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'success'
        )
      })
      .catch((res) => {
        console.log('ko', res)
        displayNotification(
          `posizione_aggiornata_error_${posizione.id}`, 
          t("Errore durante l'aggiornamento della posizione {0}", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'error'
        )
      })
  }

  const onValueChange = (e, handleChange) => {
    handleChange(e)
    setEdited(true)
  }

  useEffect(() => {
    setStatus(status)
  }, [status])

  return (
    <FlussoPersonalizzazionePosizione
      status={status}
      posizione={posizione} 
      posizioniOpen={posizioniOpen}
      setPosizioniOpen={setPosizioniOpen}
      userType={userType}
      className={cn(styles.root, posizioniOpen.includes(posizione.id) ? styles['root--open'] : '', isEditable ? styles['root--editable'] : '', className)}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <Form className={styles.form}>
            <FlussoPersonalizzazionePosizioneConfigurazione posizione={posizione} personalizzazione={personalizzazione} />
            <div className={styles.row}>
              <div className={cn(styles.section, styles['section--draft'])}>
                <p className={styles.sectionTitle}>{t('Bozza')}</p>
                <div className={cn(styles.draft, !posizione.bozzaDigitaleImage ? styles['draft--empty'] : '')}>
                  <MyAccountImagePreview 
                    id={`${posizione.id}_bozza`}
                    url={posizione.bozzaDigitaleImage?.url}
                    downloadUrl={null}
                    zoomUrl={posizione.bozzaDigitaleImage?.url}
                    name={posizione.bozzaDigitaleImage?.name}
                    size="lg"
                    previewName={t("bozza")}
                    className={styles.draftPreview}
                  />
                </div>
                {!!posizione.bozzaDigitale?.url && (
                  <Button 
                    label={t("Vedi e scarica bozza")}
                    size="small"
                    href={posizione.bozzaDigitale.url}
                    newPage
                    download
                    variant="secondary"
                    icon={<Download />}
                    icon_pos='left'
                    className={styles.draftDownload}
                  />
                )}
              </div>

              <div className={cn(styles.section, styles['section--logo'], styles[`section--loghi-n${loghiEditorClean?.length + 1}`])}>
                <p className={styles.sectionTitle}>{t('Loghi')}</p>
                <div className={styles.logo}>
                  <div className={styles.logoList}>
                    <MyAccountImagePreview 
                      url={values.logo?.url}
                      name={values.logo?.name}
                      className={styles.logoPreview}
                      onRemove={values.fileLogo ? () => {
                        setFieldValue('logo', posizione.logo)
                        setFieldValue('fileLogo', null)
                        setEdited(true)
                      } : undefined}
                    />
                    {!values.fileLogo && loghiEditorClean?.map(l => (
                      <MyAccountImagePreview 
                        url={l.url}
                        name={l.name}
                        className={styles.logoPreview}
                      />
                    ))}
                  </div>
                  {isEditable && (
                    <Alert 
                      full
                      styleVariant='basic'
                      variant='muted'
                      icon={<Info />}
                      placeholder={t("Caricare un nuovo logo richiederà la creazione di una nuova bozza digitale (come rifiutare la bozza attuale)")}
                      size="xs"
                    />
                  )}
                  {values.fileLogo ? (
                    <div>
                      <div className={styles.draftValue}>
                        <div className={styles.draftFilename}>{values.logo.name}</div>
                        {isEditable && (
                          <Button 
                            variant="ghost" 
                            label={t("Rimuovi")} 
                            ariaLabel={t("Rimuovi nuovo logo")} 
                            icon={<Bin />}
                            icon_pos='right'
                            size="small"
                            colorVariant={'danger'} 
                            onClick={() => {
                              setFieldValue('logo', posizione.logo)
                              setFieldValue('fileLogo', null)
                              setEdited(true)
                            }}
                            className={styles.draftRemoveBig}
                          />
                        )}
                      </div>
                      <p className={styles.helperText}>
                        {t('Formati consentiti: {0}', [formatiConsentitiLabel])}
                      </p>
                    </div>
                  ) : (
                    isEditable ? (
                      <div className={styles.uploadBox}>
                        <FormField className={styles.uploadButton}>
                          <FormFile
                            id={`${posizione.id}_logo`}
                            name="fileLogo"
                            // value={values.fileLogo}
                            onChange={(event) => {
                              setFieldValue(
                                'fileLogo',
                                event.target.files?.[0]
                              )
                              setFieldValue('logo', event.target.files?.[0] ? {
                                url: URL.createObjectURL(event.target.files?.[0]),
                                name: event.target.files?.[0]?.name
                              } : null)
                              setEdited(true)
                            }}
                            variant={'button'}
                            accept={logoAccept}
                            buttonProps={{ 
                              label: t('Carica nuovo logo'), 
                              variant: 'secondary', 
                              size: 'small', 
                              icon: <Upload />, 
                              iconPos: 'left' 
                            }}
                          />
                        </FormField>
                        <p className={styles.helperText}>
                          {t('Formati consentiti: {0}', [formatiConsentitiLabel])}
                        </p>
                      </div>
                    ) : <></>
                  )}
                </div>
              </div>
                
              <div className={styles.lastCol}>
                <div className={styles.note}>
                  <div className={cn(styles.section, styles['section--notaCliente'])}>
                    <div className={styles.sectionTitle}>
                      <span>{t('Nota stampa')}</span>
                      <CronologiaButton onClick={() => setLogOpen(true)} />
                    </div>
                    <FlussoPersonalizzazioneNotaValue 
                      value={posizione.log?.filter(l => l.stato === 'BOZZA_INVIATA')?.length ? (
                        posizione.log.sort((a, b) => a.created < b.created ? -1 : 1).filter(l => l.stato === 'BOZZA_INVIATA')?.[posizione.log.filter(l => l.stato === 'BOZZA_INVIATA')?.length - 1].note
                      ) : ''}
                      placeholder={t("Nessuna nota inserita dal fornitore")}
                      variant='accent'
                      className={styles.noteFornitoreValue}
                    />
                  </div>
                  <div className={cn(styles.section, styles['section--notaCaricamento'])}>
                    <p className={styles.sectionTitle}>{t('Nota')}</p>
                    <div>
                      {isEditable ? (
                      <FormField className={styles.notaCaricamentoField}>
                        <FormTextarea
                          id={`${posizione.id}_nota_caricamento`}
                          name="note"
                          value={values.note}
                          placeholder={t("Aggiungi una nota per il reparto grafico...")}
                          onChange={(e) => onValueChange(e, handleChange)}
                        />
                      </FormField>
                      ) : (
                        <FlussoPersonalizzazioneNotaValue 
                          value={posizione.log?.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length ? (
                            posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.[posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length - 1].note
                          ) : ''}
                          placeholder={t("Nessuna nota inserita dal cliente")}
                          className={styles.notaClienteValue}
                        />
                      )}
                    </div>
                  </div>
                  {isEditable && (
                    <div className={styles.ctaBox}>
                      {values.fileLogo ? (
                        <Button 
                          type="submit"
                          variant="primary" 
                          size="medium" 
                          label={t('Richiedi nuova bozza')}
                          onClick={() => setSubmitStatus('BOZZA_RIFIUTATA')}
                        />
                      ) : (
                        <>
                          <Button 
                            type="submit"
                            variant="primary" 
                            colorVariant="success"
                            size="medium" 
                            label={t('Approva bozza')}
                            icon={<Checkmark />} 
                            icon_pos='right'
                            disabled={isSubmitting}
                            // disabled={!values.note} 
                            onClick={() => setSubmitStatus('BOZZA_APPROVATA')}
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            colorVariant="danger"
                            size="medium"
                            label={t('Rifiuta bozza')}
                            icon={<Close />}
                            icon_pos='right'
                            disabled={!values.note || isSubmitting}
                            onClick={() => setSubmitStatus('BOZZA_RIFIUTATA')}
                          />
                          <Alert 
                            variant="muted" 
                            styleVariant='basic' 
                            icon={<Info />} 
                            placeholder={t("Per rifiutare una bozza è obbligatorio lasciare una nota/commento")} 
                            size='xs'
                            className={styles.alertSave} 
                          />
                        </> 
                      )}                         
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.disclaimer}>
              <p><strong>{t("IL DISEGNO DELL’ARTICOLO HA VALORE PURAMENTE ILLUSTRATIVO")}</strong></p>
              <p>
                {t("Verificare con attenzione che il prodotto sia quello scelto al momento dell’ordine e che l’impostazione dei contenuti di stampa (nomi, telefono, indirizzo, logo, ecc.) siano corretti. La richiesta massima di modifiche alle bozze grafiche è di 3, dalla 4a bozza verrà addebitato un costo aggiuntivo per ogni bozza successiva. Una volta approvata la bozza, non sarà più possibile modificare o annullare l’ordine e la data di partenza sarà aggiornata in base al giorno di approvazione. Il colore dei prodotti gadget, rispetto alla bozza inviata, potrebbe variare anche all’interno dello stesso stock a magazzino. Faremo il possibile per ottenere una resa ottimale del colore di stampa, che però potrebbe variare rispetto alla visualizzazione video.")}
                {!!condizioniServizioUrl && (
                  <>
                    {' '}
                    {t('Per qualsiasi informazione aggiuntiva, vedi:')}{' '}
                    <a href={condizioniServizioUrl} target="_blank" rel="noopener noreferrer">
                      {t('condizioni di vendita')}
                    </a>
                  </>
                )}
              </p>
            </div>
          </Form>
        )}
      </Formik>
      <MyAccountStatusLogModal id={`status_log_modal_${posizione.id}`} open={logOpen} onClose={() => setLogOpen(false)} posizione={posizione} userType={userType} />
    </FlussoPersonalizzazionePosizione>
  )
}

export default FlussoPersonalizzazioneApprovazioneBozzePosizione
